// Derive legible text colors from an admin-chosen difficulty color.
//
// A difficulty's color doubles as a soft tint background AND its label text.
// A bright pick (e.g. yellow #FFD700) is illegible as text on its own pale
// tint in light mode; a dark pick is illegible on the dark-navy tint in dark
// mode. We keep the color's hue + chroma but clamp its OKLCH *perceptual*
// lightness into a legible band per theme. The reason a naive sRGB/HSL
// approach fixes red but not yellow is that OKLCH equalises perceived
// lightness across hues — sRGB does not, so equal nominal values look far
// lighter for yellow than for red.
//
// Returns { light, dark }: wire both as CSS custom properties and switch with
// the global `.dark` class, so the text stays correct across theme toggles
// with no reactive JS. Non-hex input (e.g. 'inherit') is returned untouched.

const LIGHT_MAX_L = 0.5 // light theme: force text at least this dark
const DARK_MIN_L = 0.72 // dark theme: force text at least this light

export function difficultyTextColors(color: string): { light: string; dark: string } {
  const lch = hexToOklch(color)
  if (!lch) return { light: color, dark: color }
  const [l, c, h] = lch
  return {
    light: oklchToHex(Math.min(l, LIGHT_MAX_L), c, h),
    dark: oklchToHex(Math.max(l, DARK_MIN_L), c, h),
  }
}

function parseHex(hex: string): [number, number, number] | null {
  const m = hex.trim().match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
  if (!m) return null
  let s = m[1]
  if (s.length === 3) s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2]
  const n = parseInt(s, 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

const srgbToLinear = (c: number) =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
const linearToSrgb = (c: number) =>
  c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055

// sRGB hex → OKLCH [L, C, H] (Björn Ottosson's OKLab). Null on non-hex input.
function hexToOklch(hex: string): [number, number, number] | null {
  const rgb = parseHex(hex)
  if (!rgb) return null
  const r = srgbToLinear(rgb[0])
  const g = srgbToLinear(rgb[1])
  const b = srgbToLinear(rgb[2])
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  return [L, Math.hypot(a, bb), Math.atan2(bb, a)]
}

// OKLCH → sRGB hex, gamut-clamped into [0, 1] per channel.
function oklchToHex(L: number, C: number, H: number): string {
  const a = C * Math.cos(H)
  const b = C * Math.sin(H)
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b
  const s_ = L - 0.0894841775 * a - 1.291485548 * b
  const l = l_ * l_ * l_
  const m = m_ * m_ * m_
  const s = s_ * s_ * s_
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s
  const bl = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s
  return '#' + [r, g, bl].map((c) => toHexByte(linearToSrgb(c))).join('')
}

const toHexByte = (c: number) =>
  Math.round(Math.max(0, Math.min(1, c)) * 255)
    .toString(16)
    .padStart(2, '0')
